import { useState, useEffect } from 'react';
import { useStore } from '../store';
import { stripeApi } from '../services/api';
import { getSubscriptionStatus, formatPrice } from '../utils/subscription';
import { X, Check, Crown, Loader2, ExternalLink } from 'lucide-react';

interface PricingInfo {
  configured: boolean;
  monthly: { amount: number; currency: string };
  annual: { amount: number; currency: string };
  trialDays: number;
}

// Default pricing to show even if API fails
const DEFAULT_PRICING: PricingInfo = {
  configured: false,
  monthly: { amount: 12, currency: 'usd' },
  annual: { amount: 72, currency: 'usd' },
  trialDays: 14,
};

export default function SubscriptionModal() {
  const { subscription, showSubscriptionModal, setShowSubscriptionModal, addNotification } = useStore();
  const [pricing, setPricing] = useState<PricingInfo>(DEFAULT_PRICING);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');

  const status = getSubscriptionStatus(subscription);

  useEffect(() => {
    if (showSubscriptionModal) {
      loadPricing();
    }
  }, [showSubscriptionModal]);

  const loadPricing = async () => {
    try {
      const data = await stripeApi.getPrices();
      setPricing(data);
    } catch (error) {
      console.error('Failed to load pricing:', error);
      // Keep default pricing on error
    }
  };

  const handleSubscribe = async () => {
    if (!pricing.configured) {
      addNotification('Payment system not configured yet', 'error');
      return;
    }

    setLoading(true);
    try {
      const { url } = await stripeApi.createCheckoutSession(selectedPlan);
      if (url) {
        window.location.href = url;
      }
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Failed to start checkout', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const { url } = await stripeApi.createPortalSession();
      if (url) {
        window.location.href = url;
      }
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Failed to open billing portal', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!showSubscriptionModal) return null;

  const features = [
    'Unlimited document generation',
    'All AI providers (OpenAI, Claude, Gemini)',
    'Cloud storage integrations',
    'Export to PDF, DOCX, HTML',
    'Image generation',
    'Custom branding',
    'Priority support',
  ];

  const annualSavings = Math.round(((pricing.monthly.amount * 12) - pricing.annual.amount) / (pricing.monthly.amount * 12) * 100);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Crown className="text-accent" size={24} />
            <h2 className="text-lg font-semibold">Subscription</h2>
          </div>
          <button
            onClick={() => setShowSubscriptionModal(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Status */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Current Plan</p>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
                status.color === 'green' ? 'bg-green-100 text-green-800' :
                status.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                status.color === 'red' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {status.label}
              </span>
              {subscription?.planType && subscription.planType !== 'free' && (
                <span className="text-sm text-gray-600 capitalize">
                  ({subscription.planType})
                </span>
              )}
            </div>
          </div>

          {/* Show upgrade options if not active subscriber */}
          {subscription?.status !== 'active' && (
            <>
              {/* Plan Selection */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPlan === 'monthly'
                      ? 'border-accent bg-accent/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-semibold">Monthly</p>
                  <p className="text-2xl font-bold text-accent">
                    {formatPrice(pricing.monthly.amount)}
                  </p>
                  <p className="text-xs text-gray-500">per month</p>
                </button>

                <button
                  onClick={() => setSelectedPlan('annual')}
                  className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                    selectedPlan === 'annual'
                      ? 'border-accent bg-accent/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="absolute -top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Save {annualSavings}%
                  </span>
                  <p className="font-semibold">Annual</p>
                  <p className="text-2xl font-bold text-accent">
                    {formatPrice(pricing.annual.amount)}
                  </p>
                  <p className="text-xs text-gray-500">per year</p>
                </button>
              </div>

              {/* Features */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-3">Everything included:</p>
                <ul className="space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Subscribe Button */}
              <button
                onClick={handleSubscribe}
                disabled={loading || !pricing.configured}
                className="w-full btn btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    Subscribe Now
                    <ExternalLink size={16} />
                  </>
                )}
              </button>

              {/* Payment not configured message */}
              {!pricing.configured && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Payment processing coming soon
                </p>
              )}
            </>
          )}

          {/* Manage subscription for active subscribers */}
          {subscription?.status === 'active' && (
            <button
              onClick={handleManageSubscription}
              disabled={loading}
              className="w-full btn btn-secondary flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Opening...
                </>
              ) : (
                <>
                  Manage Subscription
                  <ExternalLink size={16} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
